<template>
    <app-layout title="SOPs">
        <form @submit.prevent="submit">
            <page-header>
                <template #title>SOPs</template>

                <template #actions>
                    <jet-button type="button" :href="route('sops.create')" :disabled="form.processing"  >
                        Add SOP
                    </jet-button>
                </template>
            </page-header>

            <div class="flex px-8">
                <div class="flex gap-x-2 w-full">
                    <div class="flex flex-col flex-1 min-w-40">
                        <jet-label for="creators" value="Creator" />
                        <multiselect
                            class="multi-sm h-7 text-base"
                            v-model="form.created_by"
                            :options="creators"
                            valueProp="id"
                            label="name"
                            placeholder="All"
                            searchable
                            @change="handleSubmit"
                        />
                    </div>

                    <div class="flex flex-col flex-1 min-w-40">
                        <jet-label for="search" value="Search" />
                        <jet-input
                            type="text"
                            class="h-7 text-base"
                            name="search"
                            placeholder="Search..."
                            v-model="form.search"
                            @input="handleSubmit"
                        />
                    </div>

                    <div class="self-end">
                        <jet-tertiary-button type="button" class="py-1 h-7" @click="handleReset" >
                            Reset
                        </jet-tertiary-button>
                    </div>
                </div>
            </div>

            <div class="px-3 md:px-4 lg:px-8 pt-3 md:pt-2 pb-4">
                <div class="rounded-t overflow-hidden border border-b-0">
                    <table class="table table-with-links w-full">
                        <thead>
                            <tr>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="title">Title</sortable-header>
                                <sortable-header class="text-left">Description</sortable-header>
                                <sortable-header class="text-left whitespace-nowrap" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="'created_by'">Created By</sortable-header>
                                <sortable-header class="text-left whitespace-nowrap" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="'created_at'">Created</sortable-header>
                                <sortable-header class="text-left whitespace-nowrap" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="'updated_by'">Updated By</sortable-header>
                                <sortable-header class="text-left whitespace-nowrap" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="'updated_at'">Updated</sortable-header>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="sop in sops.data" :key="sop.id">
                                <td class="text-left">
                                    <Link class="block px-1 md:px-2 py-1" :href="route('sops.show', sop.id)">
                                        {{ sop.title }}
                                    </Link>
                                </td>
                                <td class="text-left">
                                    <Link class="block px-1 md:px-2 py-1" :href="route('sops.show', sop.id)">
                                        {{ sop.description }}
                                    </Link>
                                </td>
                                <td class="text-left whitespace-nowrap">
                                    <Link class="block px-1 md:px-2 py-1" :href="route('sops.show', sop.id)">
                                        {{ sop.creator?.name }}
                                    </Link>
                                </td>
                                <td class="text-left whitespace-nowrap">
                                    <Link class="block px-1 md:px-2 py-1" :href="route('sops.show', sop.id)">
                                        {{ formatTimestamp(sop.created_at) }}
                                    </Link>
                                </td>
                                <td class="text-left whitespace-nowrap">
                                    <Link class="block px-1 md:px-2 py-1" :href="route('sops.show', sop.id)">
                                        {{ sop.updater?.name }}
                                    </Link>
                                </td>
                                <td class="text-left whitespace-nowrap">
                                    <Link class="block px-1 md:px-2 py-1" :href="route('sops.show', sop.id)">
                                        {{ sop.updater ? formatTimestamp(sop.updated_at) : null }}
                                    </Link>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="flex justify-between items-center mt-4">
                    <pagination class="mt-0 mb-0" :links="sops.links" />
                </div>
            </div>

            <!-- Add / Edit Sop -->
            <jet-dialog-modal :show="showSopModal" @close="closeSopModal">
                <template #title>
                     <h3 class="text-2xl">{{ showSopModal == 'add' ? 'Add' : 'Edit' }} Sop</h3>
                </template>

                <template #content>
                    <form @submit.prevent="submitForm" class="my-6">
                        <div class="mb-2">
                            <jet-label value="Title:" class="mb-0" style="min-width: 60px;" />
                            <jet-input v-model="form.title" type="text" class="w-full" />
                            <jet-input-error class="mt-1" :message="form.errors.title" />
                        </div>

                        <div>
                            <jet-label value="Description:" class="mb-0" style="min-width: 60px;" />
                            <jet-input v-model="form.description" type="text" class="w-full" />
                            <jet-input-error class="mt-1" :message="form.errors.description" />
                        </div>

                        <input type="submit" class="hidden" ref="submitFormButton" />
                    </form>

                    <h4  v-if="showSopModal !== 'add'" class="text-xl mb-1">Steps</h4>

                    <div class="flex items-start gap-4" v-if="showSopModal !== 'add'">
                        <div class="flex items-center gap-2 w-full">
                            <jet-input v-model="stepForm.description" type="text" class="w-full" />
                            <jet-input-error class="mt-1" :message="stepForm.errors.description" />
                        </div>

                        <jet-button
                            class="whitespace-nowrap"
                            @click="addStep"
                        >
                            Add Step
                        </jet-button>
                    </div>
                    <table
                        class="table w-full mt-4" 
                        v-if="selectedSop?.sop_steps?.length > 0"
                    >
                        <thead>
                            <tr>
                                <th>Description</th>
                                <th>Image</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="step in selectedSop.sop_steps" :key="step.id" class="cursor-pointer">
                                <td class="text-left">
                                    <span v-if="selectedStep.id !== step.id">{{ step.description }} </span>

                                    <div v-else>
                                        <textarea v-model="editStepForm.description" type="text" class="w-full mb-2" />

                                        <input 
                                            type="file" 
                                            ref="fileInput"
                                            accept="image/*" 
                                        />
                                    </div>
                                </td>
                                <td class="text-center">
                                    <div v-if="step.uploads?.[0]?.type === 'image'" 
                                        class="w-32 text-center"
                                    >
                                        <img
                                            :src="`/storage/${step.uploads[0].path}`" 
                                        />
                                    </div>
                                </td>
                                <td class="text-right whitespace-nowrap">
                                    <jet-secondary-button
                                        @click="setSelectedStep(step.id)"
                                        class="mr-1"
                                        v-if="selectedStep.id !== step.id"
                                    >
                                        Edit
                                    </jet-secondary-button>

                                    <jet-button
                                        v-else
                                        class="mr-1"
                                        @click="editStep(step.id)"
                                    >
                                        Save
                                    </jet-button>

                                    <jet-danger-button
                                        @click="deleteStep(step.id)"
                                    >
                                        Delete
                                    </jet-danger-button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </template>

                <template #footer>
                    <jet-danger-button v-if="showSopModal === 'edit'" class="mr-2" @click="showDeleteModal = true">
                        Delete
                    </jet-danger-button>

                    <jet-button class="mr-2" @click="$refs.submitFormButton.click()">
                        Save
                    </jet-button>

                    <jet-tertiary-button @click="closeSopModal">
                        Cancel
                    </jet-tertiary-button>
                </template>
            </jet-dialog-modal>

            <!-- Delete Sop -->
            <jet-confirmation-modal :show="showDeleteModal" @close="showDeleteModal = false">
                <template #title>
                    Delete Sop
                </template>

                <template #content>
                    Are you sure you would like to delete sop?
                </template>

                <template #footer>
                    <jet-danger-button @click="handleDeleteSop">
                        Yes
                    </jet-danger-button>

                    <jet-tertiary-button class="ml-2" @click="showDeleteModal = false">
                        No
                    </jet-tertiary-button>
                </template>
            </jet-confirmation-modal>
        </form>
    </app-layout>
</template>

<script>
import { usePage, useForm, Link } from '@inertiajs/inertia-vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetLabel from '@/Jetstream/Label';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError.vue';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetButton from '@/Jetstream/Button';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import SettingsNavigation from '@/Pages/Settings/Partials/SettingsNavigation';
import Multiselect from '@vueform/multiselect';
import Pagination from '@/Jetstream/Pagination'
import SortableHeader from '@/Shared/SortableHeader';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';

import '@vueform/multiselect/themes/default.css';

export default {
    setup(props) {
        const form = useForm({
            search: props.filters.search,
            sortColumn: props.sortColumn,
            sortDirection: props.sortDirection,
            created_by: props.filters.created_by
        });


        return { form}
    },

    components: {
        Link,
        AppLayout,
        PageHeader,
        JetLabel,
        JetInput,
        JetInputError,
        JetDialogModal,
        JetConfirmationModal,
        JetButton,
        JetDangerButton,
        JetTertiaryButton,
        SettingsNavigation,
        Multiselect,
        Pagination,
        SortableHeader,
        JetSecondaryButton
    },

    props: [
        'sops',
        'sortColumn',
        'sortDirection',
        'filters',
        'creators'
    ],

    data() {
        return {
            //
        }
    },

    methods: {
        handleSubmit: _.debounce(function () {
            this.form.get('/sops', { preserveState: true });
        }, 250),

        handleReset() {
            this.form.search = '';
            this.form.sortColumn = 'title';
            this.form.sortDirection = 'asc';
            this.form.created_by = '';
            this.handleSubmit();
        },

        handleSort(sort) {
            this.form.sortColumn = sort.column;
            this.form.sortDirection = sort.direction;
            this.handleSubmit();
        },
    },
}
</script>

<style scoped>
</style>
